// src/services/atlassianApi.js
import api from "../apiClient";
import { atlassianURL, formatAxiosError, unwrap } from "../config";

/**
 * DELETE /api/atlassian
 */
export const disconnectAtlassian = async () => {
  try {
    const res = await api.delete(`${atlassianURL}`);
    const resp = unwrap(res);
    window.location.href = resp.url;
  } catch (err) {
    throw formatAxiosError(err, "Failed to disconnect atlassian");
  }
};

/**
 * POST /api/atlassian/folders
 */
export const createFolders = async (spaceKey, jiraBoard, teamId) => {
  try {
    const res = await api.post(`${atlassianURL}/folders`, {
      teamId,
      spaceKey,
      jiraBoard,
    });
    return unwrap(res);
  } catch (err) {
    throw formatAxiosError(err, "Failed to create folders");
  }
};

/**
 * POST /api/atlassian/jira/stats
 */
export const getJiraStats = async (boardId, range, { force = false } = {}) => {
  try {
    const res = await api.post(`${atlassianURL}/jira/stats`, {
      boardId,
      range,
      force,
    });
    return unwrap(res);
  } catch (err) {
    throw formatAxiosError(err, "Failed to fetch Jira stats");
  }
};

/**
 * POST /api/atlassian/verify-space
 */
export const verifySpace = async (spaceKey) => {
  try {
    const res = await api.post(`${atlassianURL}/verify-space`, {
      spaceKey,
    });
    return unwrap(res);
  } catch (err) {
    throw formatAxiosError(err, "Failed to verify space");
  }
};

/**
 * POST /api/atlassian/verify-board
 */
export const verifyBoard = async (boardId) => {
  try {
    const res = await api.post(`${atlassianURL}/verify-board`, {
      boardId,
    });
    return unwrap(res);
  } catch (err) {
    throw formatAxiosError(err, "Failed to verify board");
  }
};

/**
 * GET /api/atlassian/spaces
 * Returns every Confluence space the org can read.
 * Shape: [{ id, key, name }, ...]
 */
export const listSpaces = async () => {
  try {
    const res = await api.get(`${atlassianURL}/spaces`);
    return unwrap(res);
  } catch (err) {
    throw formatAxiosError(err, "Failed to load Confluence spaces");
  }
};

/**
 * GET /api/atlassian/boards
 * Returns every Jira board the org can read.
 * Shape: [{ id, name, type, projectKey }, ...]
 */
export const listBoards = async () => {
  try {
    const res = await api.get(`${atlassianURL}/boards`);
    return unwrap(res);
  } catch (err) {
    throw formatAxiosError(err, "Failed to load Jira boards");
  }
};

/**
 * GET /api/atlassian/oauth/init
 * Redirects browser to Atlassian OAuth
 */
export const atlassianAuth = async (intent) => {
  try {
    const res = await api.get(`${atlassianURL}/oauth/init?intent=${intent}`);
    const resp = unwrap(res);
    window.location.href = resp.url;
  } catch (err) {
    throw formatAxiosError(err, "Failed to initiate Atlassian auth");
  }
};

export async function getJiraUsers() {
  try {
    const res = await api.get(`${atlassianURL}/jira/users`);
    return res.data;
  } catch (err) {
    throw formatAxiosError(err, "Not logged in");
  }
}

/**
 * SESSION CHECK (OAuth short-circuit)
 * - returns user if session exists
 * - throws if not logged in
 */
export async function getMe() {
  try {
    const res = await api.get(`${atlassianURL}/auth/me`);
    return res.data;
  } catch (err) {
    throw formatAxiosError(err, "Not logged in");
  }
}
